import { Plus, Trash2, GripVertical, ChevronDown, ChevronRight, Info } from "lucide-react";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { FlowPreviewPanel } from "./FlowPreviewPanel";
import { type BuilderNode, type BuilderEdge } from "./ScriptOutline";
import type { FlowNode as FlowNodeType, FlowEdge as FlowEdgeType } from "@/types/api";

/**
 * Form-Based Flow Builder
 *
 * Flexible blank canvas builder for creating workflows:
 * - Add any type of step (greeting, question, outcome, info, etc.)
 * - Each step can have pre-filled answer options
 * - Each answer branches to the next step
 * - Unlimited branching depth
 * - Drag-to-reorder steps
 * - Live preview
 */

type StepType = "message" | "question" | "outcome" | "text_entry" | "client_info";

// "message" replaced four separately-labeled types (greeting/info/transfer/
// schedule) that behaved identically at runtime — nothing anywhere ever
// branched on which of the four a step was; each was just a single-path
// step with one "Leads to". Steps saved with one of the old values still
// load fine (see the normalization in the `steps` initializer below) and
// re-save as "message" the next time they're touched — no migration
// needed, no data lost, just a smaller picker.
const LEGACY_MESSAGE_STEP_TYPES = new Set(["greeting", "info", "transfer", "schedule"]);

// "question" absorbed "selection" the same way — every call site keyed off
// `step.type === "question" || step.type === "selection"` treating them
// identically (same node_type sent to the backend, same edge-per-answer
// creation, same rendering to Staff). The only differences were cosmetic
// label text ("Selection Options" vs "Answer Options") and one validation
// asymmetry that's now resolved in favor of Selection's stricter rule:
// every answer on a branching step must have a "Leads to" set before save
// (see getSaveErrors). Steps saved as "selection" still load fine and
// re-save as "question" next time they're touched.
const LEGACY_SELECTION_STEP_TYPE = "selection";

// Bookkeeping tag stashed in FlowNode.builder_step_type (a field dedicated
// to this, never shown to Staff — see backend apps/flows/models.py::
// FlowNode). The backend's publish check (validate_flow_for_publish)
// requires the flow's one START node to have EXACTLY one outgoing edge. A
// user-authored first step can have any number of branches (a Selection
// step choosing between several locations, say), so it can never safely BE
// the START node itself. Instead every save synthesizes a dedicated,
// hidden pass-through START -> single edge -> the real first step, and
// this marker is how a later load tells "the synthetic placeholder" apart
// from "a real step that happens to be first" for flows saved before this
// existed. See the `flowPreview` builder and the `steps` initializer below.
const SYNTHETIC_START_MARKER = "synthetic_start";

// The legacy prefix builder_step_type replaced — older flows saved before
// that field existed stashed the same bookkeeping inside internal_note
// (the literal "Note for the agent" field shown to Staff), which is why
// some scripts used to show a raw "__step_type:selection" as if it were
// agent guidance. Recovery below falls back to parsing it out of there
// only when builder_step_type is empty, so those flows keep working until
// they're next saved (which upgrades them to the real field and clears
// internal_note back to blank).
const LEGACY_STEP_TYPE_PREFIX = "__step_type:";

// The synthetic START marker's own former home, for the same reason as
// LEGACY_STEP_TYPE_PREFIX above — briefly (before builder_step_type
// existed) it was stashed in internal_note as this exact string.
const LEGACY_SYNTHETIC_START_MARKER = "__synthetic_start__";

function isSyntheticStartNode(node: FlowNodeType): boolean {
  return (
    node.node_type === "START" &&
    (node.builder_step_type === SYNTHETIC_START_MARKER ||
      node.internal_note === LEGACY_SYNTHETIC_START_MARKER)
  );
}

function findSyntheticStartNode(nodes?: FlowNodeType[]): FlowNodeType | undefined {
  return nodes?.find(isSyntheticStartNode);
}

// A step is either: terminal ("outcome" — never has a next step), branching
// ("question" — one "leads to" per answer, via `answers`), or single-path —
// everything else, which has exactly one "leads to" for the whole step, via
// `nextStepId`. Written as an exclusion (rather than an explicit
// greeting/info/transfer/schedule/text_entry list) so a future step type
// defaults to single-path without a call site here forgetting it.
function isSinglePathStep(type: StepType): boolean {
  return type !== "question" && type !== "outcome";
}

export interface BuilderAnswer {
  id: string;
  text: string;
  nextStepId: string | null; // null = end this branch
}

export interface BuilderStep {
  id: string;
  type: StepType;
  title: string;
  content: string; // main text/content
  isRequired: boolean;
  isExpanded: boolean;
  answers?: BuilderAnswer[]; // for question type
  // For single-path types (see isSinglePathStep) — which step comes next.
  // null = not wired up yet (a dead end until set). Explicit rather than
  // "whatever's next in the step list" so the graph a flow of hundreds of
  // steps actually produces stays legible independent of list order — see
  // the "Leads to" selector in StepCard.
  nextStepId?: string | null;
}

export interface FormBasedFlowBuilderProps {
  onSave: (flow: { nodes: FlowNodeType[]; edges: FlowEdgeType[] }) => void | Promise<void>;
  isSaving?: boolean;
  onPreview?: (flow: { nodes: FlowNodeType[]; edges: FlowEdgeType[] }) => void;
  initialNodes?: FlowNodeType[];
  initialEdges?: FlowEdgeType[];
}

const STEP_TYPE_OPTIONS: { value: StepType; label: string; description: string }[] = [
  { value: "question", label: "Question", description: "CSR chooses from multiple options — a question with branching answers, or a list to pick from (e.g., location, department)" },
  { value: "text_entry", label: "Text Entry", description: "CSR types a short written answer instead of picking from options" },
  { value: "client_info", label: "Client Information", description: "CSR collects the caller's name, date of birth, email, and phone number — saved separately from the rest of the call log" },
  { value: "message", label: "Message", description: "A greeting, instruction, or script line for the agent — no branching, just leads to the next step" },
  { value: "outcome", label: "Outcome", description: "End call or outcome" },
];

function StepTypeSelector({
  onSelect,
}: {
  onSelect: (type: StepType, prefillAnswers?: string[]) => void;
}) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        variant="secondary"
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add New Step
      </Button>
    );
  }

  return (
    <Card className="p-4 mb-6">
      <h3 className="font-semibold mb-4">Select Step Type</h3>

      {/* Quick-start — a plain Question step with "Yes" and "No"
       * pre-filled as its two answers, nothing more. Not a distinct step
       * type: a Yes/No question already IS a 2-answer Question (renders
       * as two buttons either way), so this only saves the repetitive
       * "add answer, type Yes, add answer, type No" across a flow with
       * many of these. Kept out of STEP_TYPE_OPTIONS on purpose — that
       * list is a 1:1 map from step type to its label/badge everywhere
       * else in this file, and this isn't a second type. */}
      <Button
        onClick={() => {
          onSelect("question", ["Yes", "No"]);
          setOpen(false);
        }}
        variant="secondary"
        className="w-full justify-start h-auto py-3 px-4 text-left mb-3 border-2 border-primary/30"
      >
        <div className="flex-1">
          <div className="font-semibold">⚡ Yes/No Question</div>
          <div className="text-xs text-slate-600">
            Quick-start a Question step with "Yes" and "No" already filled in
          </div>
        </div>
      </Button>

      <div className="grid gap-2">
        {STEP_TYPE_OPTIONS.map((option) => (
          <Button
            key={option.value}
            onClick={() => {
              onSelect(option.value);
              setOpen(false);
            }}
            variant="secondary"
            className="justify-start h-auto py-3 px-4 text-left"
          >
            <div className="flex-1">
              <div className="font-semibold">{option.label}</div>
              <div className="text-xs text-slate-600">{option.description}</div>
            </div>
          </Button>
        ))}
      </div>
      <Button
        onClick={() => setOpen(false)}
        variant="ghost"
        className="w-full mt-3"
      >
        Cancel
      </Button>
    </Card>
  );
}

function AnswerBranchManager({
  ownStepId,
  answers,
  allSteps,
  onUpdate,
}: {
  ownStepId: string;
  answers: BuilderAnswer[];
  allSteps: BuilderStep[];
  onUpdate: (answers: BuilderAnswer[]) => void;
}) {
  const addAnswer = () => {
    onUpdate([
      ...answers,
      {
        id: crypto.randomUUID(),
        text: "New answer option",
        nextStepId: null,
      },
    ]);
  };

  const updateAnswer = (id: string, changes: Partial<BuilderAnswer>) => {
    onUpdate(
      answers.map((a) => (a.id === id ? { ...a, ...changes } : a))
    );
  };

  const deleteAnswer = (id: string) => {
    onUpdate(answers.filter((a) => a.id !== id));
  };

  return (
    <div className="space-y-2 mt-3 pt-3 border-t">
      <div className="text-sm font-semibold text-slate-700">
        Answer Options ({answers.length})
      </div>

      <div className="space-y-1.5">
        {answers.map((answer, idx) => (
          <div key={answer.id} className="space-y-1.5 p-2 bg-slate-50 rounded">
            <div className="flex items-start gap-2">
              <div className="text-xs text-slate-500 pt-2 min-w-fit">
                {String.fromCharCode(97 + idx)})
              </div>
              <div className="flex-1">
                <Input
                  value={answer.text}
                  onChange={(e) => updateAnswer(answer.id, { text: e.target.value })}
                  placeholder="Answer option text"
                  className="text-sm"
                  maxLength={100}
                />
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 mt-1"
                onClick={() => deleteAnswer(answer.id)}
              >
                <Trash2 className="h-3 w-3 text-danger" />
              </Button>
            </div>

            <div className="text-xs font-semibold text-slate-600 ml-6">
              Leads to:
            </div>
            <div className="ml-6">
              <select
                value={answer.nextStepId || ""}
                onChange={(e) =>
                  updateAnswer(answer.id, {
                    nextStepId: e.target.value || null,
                  })
                }
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
              >
                <option value="">-- End this branch --</option>
                {allSteps
                  .filter((s) => s.id !== ownStepId)
                  .map((step) => (
                    <option key={step.id} value={step.id}>
                      {step.title || `${step.type} (untitled)`}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        ))}
      </div>

      <Button
        onClick={addAnswer}
        variant="secondary"
        size="sm"
        className="text-xs"
      >
        <Plus className="h-3 w-3 mr-1" />
        Add Answer Option
      </Button>
    </div>
  );
}

function StepCard({
  step,
  allSteps,
  unreachableKind,
  onUpdate,
  onDelete,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  step: BuilderStep;
  allSteps: BuilderStep[];
  // "root": nothing leads to this step, anywhere — fix it here.
  // "cascade": something leads to it, but that something is itself
  // unreachable — go fix that instead; this step needs no changes of its
  // own. null: reachable, nothing to flag. See rootUnreachableIds /
  // cascadeUnreachableIds. Distinct from `destination.isWarning` below,
  // which is this step's own *outgoing* connection, not who (if anyone)
  // connects into it.
  unreachableKind: "root" | "cascade" | null;
  onUpdate: (step: BuilderStep) => void;
  onDelete: () => void;
  isDragging: boolean;
  isDragOver: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: () => void;
}) {
  const stepTypeLabel = STEP_TYPE_OPTIONS.find((o) => o.value === step.type)?.label || step.type;

  // At-a-glance destination shown next to the title when collapsed — the
  // whole point of an explicit "Leads to" is tracing a flow of hundreds of
  // steps without opening every card to see where it goes.
  let destination: { text: string; isWarning?: boolean } | null = null;
  if (step.type === "question") {
    const n = step.answers?.length || 0;
    destination = { text: `${n} option${n === 1 ? "" : "s"}` };
  } else if (step.type !== "outcome") {
    destination = step.nextStepId
      ? { text: allSteps.find((s) => s.id === step.nextStepId)?.title || "(untitled step)" }
      : { text: "Not connected yet", isWarning: true };
  }

  const toggleExpanded = () => onUpdate({ ...step, isExpanded: !step.isExpanded });

  return (
    <Card
      className={cn(
        "transition-all",
        step.isExpanded ? "p-3" : "p-2",
        isDragging && "opacity-40 bg-slate-100",
        isDragOver && "border-2 border-blue-500 bg-blue-50"
      )}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Header — a single compact row when collapsed, so a screen only
       * tall enough for a couple of the old multi-line cards can show
       * many more steps at once. */}
      <div className="flex items-center gap-2">
        <div
          draggable
          onDragStart={(e) => {
            e.stopPropagation();
            onDragStart();
          }}
          className="shrink-0 cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4 text-slate-400 hover:text-slate-600" />
        </div>

        <Badge className="shrink-0 text-[10px] px-1.5 py-0 leading-5 bg-slate-100 text-slate-700">
          {stepTypeLabel}
        </Badge>

        <button
          type="button"
          onClick={toggleExpanded}
          className="flex flex-1 min-w-0 cursor-pointer items-baseline gap-2 text-left hover:text-slate-900"
        >
          <span className="truncate text-sm font-semibold text-slate-700">
            {step.title || "(untitled)"}
          </span>
          {!step.isExpanded && destination && (
            <span
              className={cn(
                "shrink-0 truncate text-xs",
                destination.isWarning ? "text-warning" : "text-slate-400"
              )}
            >
              → {destination.text}
            </span>
          )}
        </button>

        {unreachableKind === "root" && (
          <Badge
            className="shrink-0 text-[10px] px-1.5 py-0 leading-5 bg-warning-subtle text-warning"
            title="Nothing anywhere leads here yet — point another step's Leads to (or an answer) at this one."
          >
            Not connected
          </Badge>
        )}
        {unreachableKind === "cascade" && (
          <Badge
            className="shrink-0 text-[10px] px-1.5 py-0 leading-5 bg-slate-100 text-slate-500"
            title="Something already leads here, but that step isn't connected to Start yet either. Fix that one, not this one — this reconnects on its own."
          >
            Blocked upstream
          </Badge>
        )}

        {step.isRequired && (
          <Badge className="shrink-0 text-[10px] px-1.5 py-0 leading-5 bg-blue-100 text-blue-700">
            Required
          </Badge>
        )}

        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 shrink-0"
          onClick={toggleExpanded}
        >
          {step.isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </Button>

        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 shrink-0"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5 text-danger" />
        </Button>
      </div>

      {/* Expanded Content */}
      {step.isExpanded && (
        <div className="space-y-3 mt-3">
          {/* Client Information's fields (name, last name, DOB, email,
           * phone) aren't authored here — Staff fill them in live, per
           * call — so unlike every other step type, nothing in this card
           * otherwise shows what it actually collects. Use "Live preview"
           * (right panel) to see the exact form Staff will get. */}
          {step.type === "client_info" && (
            <div className="flex gap-2 rounded-md border border-info/30 bg-info-subtle p-2.5 text-xs text-slate-700">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-info" aria-hidden="true" />
              <div>
                Collects <strong>first name, last name, date of birth, and phone number</strong>{" "}
                (required) plus <strong>email</strong> (optional) from the caller — entered live by
                Staff, not typed here. Saved to its own admin-only record, kept separate from the
                rest of this call's transcript.
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <Label className="text-xs">Title / Step Name</Label>
            <Input
              value={step.title}
              onChange={(e) => onUpdate({ ...step, title: e.target.value })}
              placeholder={`e.g., "First Question", "Greeting - Manila"`}
              className="text-sm"
              maxLength={80}
            />
          </div>

          {/* Content */}
          <div>
            <Label className="text-xs">Content / Script</Label>
            <Textarea
              value={step.content}
              onChange={(e) => onUpdate({ ...step, content: e.target.value })}
              placeholder="What should the agent say or display to the customer?"
              className="text-sm"
              rows={3}
            />
          </div>

          {/* Required Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`required-${step.id}`}
              checked={step.isRequired}
              onChange={(e) => onUpdate({ ...step, isRequired: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300"
            />
            <Label htmlFor={`required-${step.id}`} className="text-xs cursor-pointer">
              This is a required step
            </Label>
          </div>

          {/* Answer Branch Manager (for questions) */}
          {step.type === "question" && (
            <AnswerBranchManager
              ownStepId={step.id}
              answers={step.answers || []}
              allSteps={allSteps}
              onUpdate={(answers) => onUpdate({ ...step, answers })}
            />
          )}

          {/* "Leads to" for every other step type — explicit rather than
           * implicitly whatever's next in this list, so a script of
           * hundreds of steps stays traceable regardless of authoring
           * order. Outcome steps are excluded: they're an end of the
           * script by definition and can never have a next step. */}
          {isSinglePathStep(step.type) && (
            <div>
              <Label className="text-xs">Leads to</Label>
              <select
                value={step.nextStepId || ""}
                onChange={(e) =>
                  onUpdate({ ...step, nextStepId: e.target.value || null })
                }
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
              >
                <option value="">-- Not connected yet --</option>
                {allSteps
                  .filter((s) => s.id !== step.id)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.title || `${s.type} (untitled)`}
                    </option>
                  ))}
              </select>
              {!step.nextStepId && (
                <p className="text-xs text-warning mt-1">
                  Not connected yet — this will be a dead end until you pick a next step.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export function FormBasedFlowBuilder({
  onSave,
  isSaving,
  onPreview,
  initialNodes,
  initialEdges,
}: FormBasedFlowBuilderProps) {
  // Convert existing nodes/edges to BuilderStep format for editing
  const [draggedStepId, setDraggedStepId] = useState<string | null>(null);
  const [dragOverStepId, setDragOverStepId] = useState<string | null>(null);

  // Stable id for the synthetic START passthrough node (see
  // SYNTHETIC_START_MARKER above) — reused across saves of this flow
  // rather than minted fresh every time, same identity-survives-a-save
  // reasoning FlowViewSet.graph applies to every other node. Computed once
  // at mount: reuse the existing synthetic node's id if this flow already
  // has one, otherwise (a brand new flow, or a legacy flow saved before
  // this existed) mint a fresh one that the first save will create.
  const [startNodeId] = useState<string>(
    () => findSyntheticStartNode(initialNodes)?.id ?? crypto.randomUUID()
  );

  const [steps, setSteps] = useState<BuilderStep[]>(() => {
    if (initialNodes && initialNodes.length > 0) {
      // The API returns nodes in whatever order the DB feels like (FlowNode
      // has no defined ordering) — not save order, not START-first. The
      // save side already stamps each node's `position_y` with its index
      // in the step list (see the `flowPreview` builder below), so sort by
      // that to rebuild the list in the order it was actually arranged in,
      // rather than an arbitrary one that ignores drag-to-reorder entirely
      // on reload. The synthetic START placeholder itself is filtered out
      // here — it isn't a user-editable step, see SYNTHETIC_START_MARKER.
      const orderedNodes = [...initialNodes]
        .filter((n) => !isSyntheticStartNode(n))
        .sort((a, b) => a.position_y - b.position_y);
      return orderedNodes.map((node) => {
        const nodeEdges = initialEdges?.filter((e) => e.source_node === node.id) || [];
        const answers: BuilderAnswer[] = nodeEdges.map((edge) => ({
          id: edge.id,
          text: edge.label,
          nextStepId: edge.target_node,
        }));

        // Recover step type: builder_step_type is the real source of
        // truth. Older flows saved before that field existed stashed the
        // same tag inside internal_note instead (see
        // LEGACY_STEP_TYPE_PREFIX) — fall back to parsing it from there so
        // those scripts don't lose their step type until next saved.
        let stepType: StepType;
        if (node.builder_step_type) {
          stepType = node.builder_step_type as StepType;
        } else if (node.internal_note?.startsWith(LEGACY_STEP_TYPE_PREFIX)) {
          stepType = node.internal_note.replace(LEGACY_STEP_TYPE_PREFIX, "") as StepType;
        } else {
          // Fallback: infer from node_type
          stepType = node.node_type === "START" ? "message" : node.node_type === "END" ? "outcome" : "question";
        }
        // Normalize the four merged-away types (see LEGACY_MESSAGE_STEP_TYPES)
        // so a flow saved before the merge shows "Message" immediately on
        // reload rather than a raw fallback label, and re-saves as
        // "message" from here on without needing to be touched first.
        if (LEGACY_MESSAGE_STEP_TYPES.has(stepType)) {
          stepType = "message";
        }
        // Normalize the merged-away "selection" type (see
        // LEGACY_SELECTION_STEP_TYPE) the same way.
        if ((stepType as string) === LEGACY_SELECTION_STEP_TYPE) {
          stepType = "question";
        }

        return {
          id: node.id,
          type: stepType,
          title: node.title,
          content: node.spiel_text,
          isRequired: false,
          isExpanded: false,
          answers: answers.length > 0 ? answers : undefined,
          // Single-path steps have exactly one outgoing edge (the one this
          // builder itself creates from the "Leads to" selector) — recover
          // it from the real saved edge, not from list position, so
          // reopening a flow shows the connections it actually has.
          nextStepId: isSinglePathStep(stepType) ? (nodeEdges[0]?.target_node ?? null) : undefined,
        };
      });
    }
    return [];
  });

  // Generate flow preview
  const flowPreview = useMemo(() => {
    const nodes: FlowNodeType[] = [];
    const edges: FlowEdgeType[] = [];

    if (steps.length === 0) {
      return { nodes, edges };
    }

    // The one required entry point — always a plain pass-through with
    // exactly one outgoing edge into the real first step, regardless of
    // how many branches that first step has. The backend's publish check
    // (validate_flow_for_publish) rejects a START node with any count
    // other than 1, so the user's first step can never safely BE START
    // itself — a first step with more than one branch (a Selection
    // choosing between locations, say) would make the flow unpublishable,
    // and Staff would only ever reach whichever branch happened to load
    // first. See SYNTHETIC_START_MARKER above.
    nodes.push({
      id: startNodeId,
      title: "Start",
      title_es: "Start",
      node_type: "START",
      spiel_text: "",
      spiel_text_es: "",
      position_x: 100,
      position_y: 0,
      internal_note: "",
      internal_note_es: "",
      condition_text: "",
      condition_text_es: "",
      builder_step_type: SYNTHETIC_START_MARKER,
    });
    edges.push({
      id: `edge-${startNodeId}-first`,
      source_node: startNodeId,
      target_node: steps[0].id,
      label: "",
      label_es: "",
      sort_order: 0,
    });

    let posY = 150;

    // Create nodes for each step
    steps.forEach((step, idx) => {
      const nodeType: "DECISION" | "END" = step.type === "outcome" ? "END" : "DECISION";

      const node: FlowNodeType = {
        id: step.id,
        title: step.title || `${step.type} ${idx + 1}`,
        title_es: step.title || `${step.type} ${idx + 1}`,
        node_type: nodeType,
        spiel_text: step.content,
        spiel_text_es: step.content,
        position_x: 100,
        position_y: posY,
        // internal_note is real agent-facing guidance (shown as "Note for
        // the agent" in the Staff player) — this builder has no UI to
        // author that yet, so it's left blank rather than repurposed for
        // bookkeeping. Step type recovery uses builder_step_type instead.
        internal_note: "",
        internal_note_es: "",
        condition_text: "",
        condition_text_es: "",
        builder_step_type: step.type,
      };
      nodes.push(node);
      posY += 150;

      // Create edges for answers (question type with answers)
      if (step.type === "question" && step.answers && step.answers.length > 0) {
        step.answers.forEach((answer, answerIdx) => {
          if (answer.nextStepId) {
            const edge: FlowEdgeType = {
              id: `edge-${answer.id}`,
              source_node: step.id,
              target_node: answer.nextStepId,
              label: answer.text,
              label_es: answer.text,
              sort_order: answerIdx,
            };
            edges.push(edge);
          }
        });
      } else if (isSinglePathStep(step.type) && step.nextStepId) {
        // Single-path steps connect via the explicit "Leads to" selector
        // (see isSinglePathStep and StepCard) rather than "whatever's next
        // in this list" — the step list's order is purely an authoring
        // convenience now, not something the graph depends on. A step
        // with "Leads to" left unset (or a question with no answers yet, or
        // an outcome step, always a dead end) simply has
        // no outgoing edge until that's fixed; the publish check
        // (validate_flow_for_publish) already catches an incomplete graph
        // like that, same as it always has.
        const edge: FlowEdgeType = {
          id: `edge-${step.id}-next`,
          source_node: step.id,
          target_node: step.nextStepId,
          label: "Continue",
          label_es: "Continue",
          sort_order: 0,
        };
        edges.push(edge);
      }
    });

    return { nodes, edges };
  }, [steps, startNodeId]);

  // Which steps nothing actually leads to — same BFS-from-Start
  // reachability check the backend runs at publish time
  // (validate_flow_for_publish), just run here too so a step that's been
  // authored but never wired in as anyone's "Leads to"/answer target shows
  // up immediately while editing, instead of only after Save → Publish →
  // reading the error. A step can look completely fine on its own card
  // (its own "Leads to" set, no missing fields) and still be unreachable —
  // the problem is nothing points *to* it, which no per-step field alone
  // can reveal.
  //
  // Unreachable steps split into two very different situations, which a
  // flat list can't tell apart and a real script full of hundreds of steps
  // makes genuinely confusing to untangle by hand:
  //  - "root" — literally nothing anywhere points to this step. This is
  //    the actual thing to go fix.
  //  - "cascade" — something DOES lead to this step, but that something
  //    is itself unreachable, so the path never actually gets here either.
  //    Fixing the root step(s) upstream reconnects every cascade step
  //    hanging off it in one shot — there's nothing to individually wire
  //    on a cascade step itself.
  // (What just happened with Pregnancy Test → LMP: Pregnancy Test was the
  // root — Call for Who's "Self" answer wasn't pointed at it yet — and LMP
  // was a cascade step that looked broken purely because Pregnancy Test,
  // the only thing leading to it, was.)
  const { rootUnreachableIds, cascadeUnreachableIds } = useMemo(() => {
    const outgoing = new Map<string, string[]>();
    const incomingCount = new Map<string, number>();
    for (const edge of flowPreview.edges) {
      const targets = outgoing.get(edge.source_node) ?? [];
      targets.push(edge.target_node);
      outgoing.set(edge.source_node, targets);
      incomingCount.set(edge.target_node, (incomingCount.get(edge.target_node) ?? 0) + 1);
    }
    const visited = new Set([startNodeId]);
    const queue = [startNodeId];
    while (queue.length > 0) {
      const current = queue.shift()!;
      for (const target of outgoing.get(current) ?? []) {
        if (!visited.has(target)) {
          visited.add(target);
          queue.push(target);
        }
      }
    }
    const root = new Set<string>();
    const cascade = new Set<string>();
    for (const s of steps) {
      if (visited.has(s.id)) continue;
      if ((incomingCount.get(s.id) ?? 0) === 0) {
        root.add(s.id);
      } else {
        cascade.add(s.id);
      }
    }
    return { rootUnreachableIds: root, cascadeUnreachableIds: cascade };
  }, [flowPreview, startNodeId, steps]);

  // Convert to BuilderNode/BuilderEdge format for FlowPreviewPanel
  const previewNodes: BuilderNode[] = flowPreview.nodes.map((n) => ({
    id: n.id,
    node_type: n.node_type,
    title: n.title,
    title_es: n.title_es,
    spiel_text: n.spiel_text,
    spiel_text_es: n.spiel_text_es,
    internal_note: n.internal_note,
    internal_note_es: n.internal_note_es,
    condition_text: n.condition_text,
    condition_text_es: n.condition_text_es,
    builder_step_type: n.builder_step_type,
  }));

  const previewEdges: BuilderEdge[] = flowPreview.edges.map((e) => ({
    id: e.id,
    source: e.source_node,
    target: e.target_node,
    label: e.label,
    label_es: e.label_es,
    sort_order: e.sort_order,
  }));

  // `prefillAnswers` is only used by the Yes/No quick-start above — plain
  // answer text, each starting unwired (nextStepId null) same as if the
  // admin had clicked "Add Answer Option" and typed it themselves.
  const addStep = (type: StepType, prefillAnswers?: string[]) => {
    const newStep: BuilderStep = {
      id: crypto.randomUUID(),
      type,
      title: "",
      content: "",
      isRequired: false,
      isExpanded: true,
      answers:
        type === "question"
          ? (prefillAnswers ?? []).map((text) => ({
              id: crypto.randomUUID(),
              text,
              nextStepId: null,
            }))
          : undefined,
      nextStepId: isSinglePathStep(type) ? null : undefined,
    };
    setSteps([...steps, newStep]);
  };

  const updateStep = (updatedStep: BuilderStep) => {
    setSteps(steps.map((s) => (s.id === updatedStep.id ? updatedStep : s)));
    onPreview?.(flowPreview);
  };

  const deleteStep = (stepId: string) => {
    // Both filtering the deleted step out and clearing every other step's
    // reference to it happen in one update — the second read of `steps`
    // this used to do (a separate setSteps(prev => ...) call right after)
    // was redundant since React already guarantees a functional updater
    // sees the latest pending state within the same batch.
    setSteps((prev) =>
      prev
        .filter((s) => s.id !== stepId)
        .map((s) => ({
          ...s,
          answers: s.answers?.map((a) =>
            a.nextStepId === stepId ? { ...a, nextStepId: null } : a
          ),
          nextStepId: s.nextStepId === stepId ? null : s.nextStepId,
        }))
    );
  };

  const handleDragStart = (stepId: string) => {
    setDraggedStepId(stepId);
  };

  const handleDragOver = (e: React.DragEvent, stepId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStepId(stepId);
  };

  const handleDragLeave = () => {
    setDragOverStepId(null);
  };

  const handleDrop = (targetStepId: string) => {
    if (!draggedStepId || draggedStepId === targetStepId) {
      setDraggedStepId(null);
      setDragOverStepId(null);
      return;
    }

    const draggedIdx = steps.findIndex((s) => s.id === draggedStepId);
    const targetIdx = steps.findIndex((s) => s.id === targetStepId);

    if (draggedIdx === -1 || targetIdx === -1) {
      setDraggedStepId(null);
      setDragOverStepId(null);
      return;
    }

    const newSteps = [...steps];
    const [draggedStep] = newSteps.splice(draggedIdx, 1);
    newSteps.splice(targetIdx, 0, draggedStep);

    setSteps(newSteps);
    setDraggedStepId(null);
    setDragOverStepId(null);
  };

  // Validation for save
  const getSaveErrors = (): string[] => {
    const errors: string[] = [];

    if (steps.length === 0) {
      errors.push("Add at least one step");
      return errors;
    }

    steps.forEach((step, idx) => {
      // Check required steps
      if (step.isRequired && (!step.title || !step.content)) {
        errors.push(`Step ${idx + 1} (${step.title || "untitled"}) is required but missing title or content`);
      }

      // Check question steps - all answers must have a "leads to" step
      // (this was Selection's rule before the two types merged — kept as
      // the stricter of the two so a branch can never silently dead-end).
      if (step.type === "question") {
        if (!step.answers || step.answers.length === 0) {
          errors.push(`Step ${idx + 1} (${step.title || "Question"}) has no answer options defined`);
        }
        step.answers?.forEach((answer) => {
          if (!answer.nextStepId) {
            errors.push(`Step ${idx + 1} (${step.title || "Question"}), answer "${answer.text}" must have a "Leads to" step selected`);
          }
        });
      }
    });

    return errors;
  };

  const saveErrors = getSaveErrors();
  const canSave = steps.length > 0 && saveErrors.length === 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Builder */}
      <div className="md:col-span-2 flex flex-col">
        <div className="space-y-1 pb-3">
          <div>
            <h2 className="text-lg font-semibold mb-0.5">Build Your Workflow</h2>
            <p className="text-sm text-slate-600">
              Add steps, configure answers, and define branches. Steps are processed in order.
            </p>
          </div>
        </div>

        {/* Every step this catches is also individually badged below (a
         * "Not connected" root, or "Blocked upstream" cascade) — this is
         * the same steps gathered in one place, so fixing a script of
         * hundreds of steps doesn't mean scanning the whole list by eye
         * (or hitting Save/Publish repeatedly) to find them all. Split into
         * root vs. cascade specifically because a flat list can't tell you
         * which of a dozen flagged steps to actually go touch — fixing one
         * root step can clear out an entire branch of cascade ones at
         * once, same as Pregnancy Test being the one real fix that also
         * cleared LMP, Zip Code, etc. Not a save-blocker: a step
         * mid-authoring is expected to be disconnected for a bit before
         * its "Leads to" gets set. */}
        {(rootUnreachableIds.size > 0 || cascadeUnreachableIds.size > 0) && (
          <Card className="mb-3 border-warning/30 bg-warning-subtle p-3">
            {rootUnreachableIds.size > 0 && (
              <div>
                <div className="text-xs font-semibold text-warning">
                  {rootUnreachableIds.size} step{rootUnreachableIds.size === 1 ? "" : "s"} with
                  nothing leading to {rootUnreachableIds.size === 1 ? "it" : "them"} — fix these
                </div>
                <p className="mt-1 text-xs text-slate-600">
                  Staff will never actually reach{" "}
                  {rootUnreachableIds.size === 1 ? "this step" : "these steps"} during a call. Point
                  another step's "Leads to" (or a Question's answer) at{" "}
                  {rootUnreachableIds.size === 1 ? "it" : "each one"}, or delete{" "}
                  {rootUnreachableIds.size === 1 ? "it if it's" : "them if they're"} not needed.
                </p>
                <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-xs text-slate-700">
                  {steps
                    .filter((s) => rootUnreachableIds.has(s.id))
                    .map((s) => (
                      <li key={s.id}>• {s.title || "(untitled)"}</li>
                    ))}
                </ul>
              </div>
            )}

            {cascadeUnreachableIds.size > 0 && (
              <div className={rootUnreachableIds.size > 0 ? "mt-3 border-t border-warning/20 pt-3" : ""}>
                <div className="text-xs font-semibold text-slate-500">
                  {cascadeUnreachableIds.size} more step{cascadeUnreachableIds.size === 1 ? "" : "s"}{" "}
                  blocked behind {rootUnreachableIds.size > 0 ? "the above" : "an earlier disconnected step"}
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  These are wired correctly on their own — nothing to fix on them directly. They'll
                  reconnect automatically once whatever leads to them, further up the chain, is
                  fixed.
                </p>
                <ul className="mt-2 max-h-32 space-y-0.5 overflow-y-auto text-xs text-slate-500">
                  {steps
                    .filter((s) => cascadeUnreachableIds.has(s.id))
                    .map((s) => (
                      <li key={s.id}>• {s.title || "(untitled)"}</li>
                    ))}
                </ul>
              </div>
            )}
          </Card>
        )}

        {/* Steps list — scrolls with the page itself (not a separate inner
         * scroll region). A fixed h-screen box here used to extend below
         * the visible viewport (this column doesn't start at the top of
         * the page — the header and Workflow Details card sit above it),
         * so its own scrollbar could move without bringing anything on
         * screen into view; only scrolling the actual page got you there.
         * The sticky footer below still works fine against the page's own
         * scroll — sticky doesn't need a scrolling ancestor box. */}
        <div className="mb-4">
          {steps.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-slate-600 mb-4">No steps yet. Start building your workflow.</p>
              <StepTypeSelector onSelect={addStep} />
            </Card>
          ) : (
            <div className="space-y-1.5">
              {steps.map((step) => (
                <StepCard
                  key={step.id}
                  step={step}
                  allSteps={steps}
                  unreachableKind={
                    rootUnreachableIds.has(step.id)
                      ? "root"
                      : cascadeUnreachableIds.has(step.id)
                        ? "cascade"
                        : null
                  }
                  onUpdate={updateStep}
                  onDelete={() => deleteStep(step.id)}
                  isDragging={draggedStepId === step.id}
                  isDragOver={dragOverStepId === step.id}
                  onDragStart={() => handleDragStart(step.id)}
                  onDragOver={(e) => handleDragOver(e, step.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={() => handleDrop(step.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Sticky buttons at bottom */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 space-y-3 mt-auto">
          {saveErrors.length > 0 && (
            <Card className="p-3 border-red-200 bg-red-50">
              <div className="text-xs font-semibold text-red-700 mb-2">❌ Can't save yet:</div>
              <ul className="text-xs text-red-600 space-y-1 ml-2">
                {saveErrors.map((error, idx) => (
                  <li key={idx}>• {error}</li>
                ))}
              </ul>
            </Card>
          )}
          {steps.length > 0 && (
            <StepTypeSelector onSelect={addStep} />
          )}
          <Button
            onClick={() => onSave(flowPreview)}
            disabled={!canSave}
            loading={isSaving}
            className="w-full"
          >
            Save Workflow
          </Button>
        </div>
      </div>

      {/* Preview */}
      <div className="md:col-span-1">
        <div className="sticky top-4">
          <h3 className="font-semibold mb-4">Preview</h3>
          {steps.length > 0 ? (
            <FlowPreviewPanel
              flowTitle="Workflow Preview"
              nodes={previewNodes}
              edges={previewEdges}
            />
          ) : (
            <Card className="p-4 text-center text-sm text-slate-600">
              Add steps to see preview
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
